import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Box, DisplayPosition, withCitrusTheme, ZIndex } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { ScrollableBasket } from '@features/scrollable-basket'
import type { ScrollableBasketRecipe } from '@features/scrollable-basket'
import type { RecipeUUID } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'
import { incrementTutorialViewed } from 'actions/tutorial'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { checkoutBasket } from 'routes/Menu/actions/menuCheckoutClick'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import { useBasketCheckoutDiscount } from 'routes/Menu/experiments/GoustoAnytime/useBasketCheckoutDiscount'
import { useIsTutorialPending } from 'routes/Menu/hooks/useIsTutorialPending'
import {
  getBasketDate,
  getBasketMenuUuid,
  getBasketOrderId,
  getBasketRecipePositions,
  getBasketSlotId,
  getNumPortions,
} from 'selectors/basket'
import { getBrowserType } from 'selectors/browser'
import { createGetActionTypeIsPending } from 'selectors/status'

import { useCheckoutButton } from '../../hooks/useCheckoutButton'
import { ViewBasketModal } from '../ScrollableBasket/ViewBasketModal'

const Container = withCitrusTheme(Box, () => ({
  margin: '0 auto',
}))

const BlurredBackground = withCitrusTheme(Box, () => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(30px)',
}))

type BasketContainerProps = {
  onRecipeClick?: (recipeId: RecipeUUID, recipeTitle: string) => void
}

const useRemoveNotLoadedRecipesEffect = () => {
  const { basketRecipes } = useBasketRecipes()
  const { removeRecipeByUuid } = useBasket()

  const { request } = useMenuMeta()
  const basketDate = useSelector(getBasketDate)

  useEffect(() => {
    if (!request || !basketDate) return

    const isCurrentMenuLoaded = basketDate === request.deliveryDate
    if (!isCurrentMenuLoaded) {
      return
    }

    basketRecipes.forEach(({ id, recipe }) => {
      if (!recipe) {
        removeRecipeByUuid(id, undefined, undefined, {
          markUnsaved: false,
        })
      }
    })
  }, [removeRecipeByUuid, basketRecipes, request, basketDate])
}

export const BasketContainer = ({ onRecipeClick }: BasketContainerProps = {}) => {
  const isLoading = useSelector(createGetActionTypeIsPending(actionTypes.MENU_FETCH_DATA))
  const { isCheckoutButtonDisabled, isCheckoutButtonPending, isCheckoutClientGuardDisabled } =
    useCheckoutButton()
  const { basketRecipes } = useBasketRecipes()
  const recipePositions = useSelector(getBasketRecipePositions)
  const slotId = useSelector(getBasketSlotId)
  const basketOrderId = useSelector(getBasketOrderId)
  const basketDate = useSelector(getBasketDate)
  const menuUuid = useSelector(getBasketMenuUuid)
  const basketNumPortions = useSelector(getNumPortions)
  const isTooltipPending = useIsTutorialPending('basket-tooltip')
  const [viewBasketModalOpen, setViewBasketModalOpen] = useState(false)

  const { recipeCount, getQuantitiesForRecipeId } = useBasket()

  const selectedRecipes = useMemo<ScrollableBasketRecipe[]>(() => {
    const recipes: ScrollableBasketRecipe[] = []

    basketRecipes.forEach(({ recipe, numPortions }) => {
      if (recipe) {
        const quantity = getQuantitiesForRecipeId(recipe.id)
        for (let i = 0; i < quantity; i++) {
          recipes.push({ recipe, numPortions })
        }
      }
    })

    return recipes
  }, [basketRecipes, getQuantitiesForRecipeId])

  const sortedSelectedRecipes = useMemo(() => {
    if (!recipePositions?.size) {
      return selectedRecipes
    }

    const recipesById = new Map(selectedRecipes.map((item) => [item.recipe.id, item]))

    // Build ordered recipes based on recipePosition
    const orderedRecipes: ScrollableBasketRecipe[] = recipePositions
      .map((id) => recipesById.get(id))
      .filter((r: ScrollableBasketRecipe | undefined): r is ScrollableBasketRecipe => Boolean(r))
      .toArray()

    // Count how many times each recipe has been placed
    const placedCounts = orderedRecipes.reduce<Record<string, number>>((acc, { recipe }) => {
      acc[recipe.id] = (acc[recipe.id] ?? 0) + 1

      return acc
    }, {})

    // Filter out recipes already accounted for in orderedRecipes
    const leftoverRecipes = selectedRecipes.filter(({ recipe }) => {
      if (placedCounts[recipe.id]) {
        placedCounts[recipe.id] -= 1

        return false
      }

      return true
    })

    return [...leftoverRecipes, ...orderedRecipes]
  }, [recipePositions, selectedRecipes])

  useRemoveNotLoadedRecipesEffect()

  const dispatch = useDispatch()
  const { pricing } = usePricing()
  const discount = useBasketCheckoutDiscount(pricing)
  const view = useSelector(getBrowserType)
  const section = trackingKeys.menu

  const handleCheckout = useCallback(
    (e) => {
      e.stopPropagation()
      if (!isCheckoutButtonPending) {
        datadogRum.addAction('basket_checkout_button_clicked', {
          slotId,
          basketDate,
          basketOrderId,
        })
        dispatch(
          checkoutBasket({
            section,
            view,
            pricing,
            discount,
          }),
        )
      }
    },
    [
      dispatch,
      section,
      view,
      pricing,
      discount,
      isCheckoutButtonPending,
      slotId,
      basketDate,
      basketOrderId,
    ],
  )

  const handleRecipeClick = useCallback(
    (recipeUuid: RecipeUUID) => {
      if (!onRecipeClick) {
        dispatch(showDetailRecipe(recipeUuid))

        return
      }

      const selectedRecipe = selectedRecipes.find(({ recipe }) => recipe.id === recipeUuid)
      if (selectedRecipe) {
        onRecipeClick(recipeUuid, selectedRecipe.recipe.name)
      }
    },
    [dispatch, onRecipeClick, selectedRecipes],
  )

  const handleOpenBasket = useCallback(() => {
    datadogRum.addAction('open_basket', {
      menuId: menuUuid,
      deliveryDate: basketDate,
      basketRecipeCount: selectedRecipes.length,
    })
    setViewBasketModalOpen(true)
  }, [basketDate, menuUuid, selectedRecipes.length])

  // If there are no recipes in the basket, don't show the basket
  if (recipeCount <= 0 || isLoading) {
    return null
  }

  return (
    <BlurredBackground
      bottom="0"
      left="0"
      position={DisplayPosition.Fixed}
      width="100%"
      zIndex={ZIndex.Sticky}
    >
      <Container maxWidth="1600px">
        {viewBasketModalOpen && (
          <ViewBasketModal
            isCheckoutClientGuardDisabled={isCheckoutClientGuardDisabled}
            isCheckoutDisabled={isCheckoutButtonDisabled}
            isCheckoutPending={isCheckoutButtonPending}
            onCloseModal={() => setViewBasketModalOpen(false)}
            onCheckout={handleCheckout}
          />
        )}
        <ScrollableBasket
          basketNumPortions={basketNumPortions}
          isCheckoutDisabled={isCheckoutButtonDisabled}
          isLoading={isLoading}
          onClickViewBasket={handleOpenBasket}
          onRecipeClick={(recipeUuid) => handleRecipeClick(recipeUuid)}
          selectedRecipes={sortedSelectedRecipes}
          showTooltip={isTooltipPending}
          onCloseTooltip={() => dispatch(incrementTutorialViewed('basket-tooltip'))}
        />
      </Container>
    </BlurredBackground>
  )
}
